/** Inline SVG icons (24x24 viewBox, currentColor). All decorative: aria-hidden. */
import { fromHtml } from './dom';

function gearPath(cx: number, cy: number, rOuter: number, rRoot: number, teeth: number, rHole: number): string {
  const step = (Math.PI * 2) / teeth;
  const baseHalf = step * 0.3;
  const topHalf = step * 0.17;
  const pt = (r: number, a: number) => `${(cx + r * Math.sin(a)).toFixed(2)} ${(cy - r * Math.cos(a)).toFixed(2)}`;
  let d = '';
  for (let i = 0; i < teeth; i++) {
    const a = i * step;
    d += `${i === 0 ? 'M' : 'L'}${pt(rRoot, a - baseHalf)}`;
    d += `L${pt(rOuter, a - topHalf)}`;
    d += `A${rOuter} ${rOuter} 0 0 1 ${pt(rOuter, a + topHalf)}`;
    d += `L${pt(rRoot, a + baseHalf)}`;
    d += `A${rRoot} ${rRoot} 0 0 1 ${pt(rRoot, a + step - baseHalf)}`;
  }
  d += 'Z';
  d += `M${cx + rHole} ${cy}A${rHole} ${rHole} 0 1 0 ${cx - rHole} ${cy}A${rHole} ${rHole} 0 1 0 ${cx + rHole} ${cy}Z`;
  return d;
}

const GEAR_D = gearPath(12, 12, 10.6, 7.9, 8, 3.3);

const ICONS = {
  gear: `<svg viewBox="0 0 24 24"><path fill="currentColor" fill-rule="evenodd" d="${GEAR_D}"/></svg>`,
  close: `<svg viewBox="0 0 24 24"><path d="M6 6L18 18M18 6L6 18" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" fill="none"/></svg>`,
  exit: `<svg viewBox="0 0 24 24"><path d="M6.5 6.5L17.5 17.5M17.5 6.5L6.5 17.5" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" fill="none"/></svg>`,
  /** Navigation arrow pointing up (= straight ahead). */
  arrow: `<svg viewBox="0 0 24 24"><path d="M12 1.8L20.2 21.2L12 16.6L3.8 21.2Z" fill="currentColor" stroke="rgba(0,0,0,.55)" stroke-width="1.1" stroke-linejoin="round"/></svg>`,
  back: `<svg viewBox="0 0 24 24"><path d="M15 4.5L7.5 12L15 19.5" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>`,
  chevron: `<svg viewBox="0 0 24 24"><path d="M9 4.5L16.5 12L9 19.5" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>`,
  expand: `<svg viewBox="0 0 24 24"><path d="M14 4h6v6M10 20H4v-6M20 4l-7 7M4 20l7-7" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>`,
  gps: `<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="6.2" stroke="currentColor" stroke-width="2.2" fill="none"/><circle cx="12" cy="12" r="2.4" fill="currentColor"/><path d="M12 1.5v4M12 18.5v4M1.5 12h4M18.5 12h4" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>`,
  warn: `<svg viewBox="0 0 24 24"><path d="M12 2.8L22.4 20.6H1.6Z" fill="currentColor"/><path d="M12 9v5.6" stroke="#111" stroke-width="2.4" stroke-linecap="round"/><circle cx="12" cy="17.6" r="1.4" fill="#111"/></svg>`,
  info: `<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="currentColor"/><path d="M12 10.5v6.5" stroke="#111" stroke-width="2.4" stroke-linecap="round"/><circle cx="12" cy="7.2" r="1.4" fill="#111"/></svg>`,
  camera: `<svg viewBox="0 0 24 24"><path d="M3 8.2A2.2 2.2 0 0 1 5.2 6h2.3l1.6-2.2h5.8L16.5 6h2.3A2.2 2.2 0 0 1 21 8.2v9.6a2.2 2.2 0 0 1-2.2 2.2H5.2A2.2 2.2 0 0 1 3 17.8Z" fill="currentColor"/><circle cx="12" cy="12.8" r="3.9" fill="none" stroke="#b1040f" stroke-width="2"/></svg>`,
  play: `<svg viewBox="0 0 24 24"><path d="M7 4.5L19.5 12L7 19.5Z" fill="currentColor"/></svg>`,
  reset: `<svg viewBox="0 0 24 24"><path d="M4.5 12a7.5 7.5 0 1 0 2.4-5.5" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" fill="none"/><path d="M4 3.5v5h5" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>`,
  checker: `<svg viewBox="0 0 24 24"><rect x="2" y="5" width="20" height="14" rx="2" fill="#fff"/><path d="M2 5h5v4.7H2zM12 5h5v4.7h-5zM7 9.7h5v4.6H7zM17 9.7h5v4.6h-5zM2 14.3h5V19H2zM12 14.3h5V19h-5z" fill="#111"/></svg>`,
} as const;

export type IconName = keyof typeof ICONS;

export function icon(name: IconName, cls = 'uz-icon'): SVGSVGElement {
  const el = fromHtml<SVGSVGElement>(ICONS[name]);
  el.setAttribute('class', cls);
  el.setAttribute('aria-hidden', 'true');
  el.setAttribute('focusable', 'false');
  return el;
}
